import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { CasinoConfigType } from '../../../api/fetchers/casinoConfig/types';
import type { EnvConfigType } from '../../../api/fetchers/env/types';
import type { Compliance, PlayerConfigType } from '../../../api/fetchers/playerConfig/types';
import type { RtpType } from '../../../api/fetchers/rtp/types';
import type { SessionBalanceResponse } from '../../../api/fetchers/sessionBalance/types';
import type { StatsSettingsType } from '../../../api/fetchers/settings/types';
import type { StatsConfigType } from '../../../api/fetchers/stats/types';
import type { TableConfigType } from '../../../api/fetchers/tableConfig/types';
import type { RealityCheckType, VersionsConfigType } from '../../../api/fetchers/versions/types';
import type { IUserSession } from '../../../config/UserSession';
import { UserSession } from '../../../config/UserSession';
import { Logger } from '../../../logger/Logger';
import { SessionStorage } from '../../../storage/session/SessionStorage';
import { fetchCasinoConfigAction } from '../../apiActions/fetchCasinoConfigAction';
import { fetchEnvAction } from '../../apiActions/fetchEnvAction';
import { fetchPlayerConfigAction } from '../../apiActions/fetchPlayerConfigAction';
import { fetchRtpAction } from '../../apiActions/fetchRtpAction';
import { fetchStatsConfigAction } from '../../apiActions/fetchStatsConfigAction';
import { fetchStatsSettingsAction } from '../../apiActions/fetchStatsSettingsAction';
import { fetchTableConfigAction } from '../../apiActions/fetchTableConfigAction';
import { fetchVersionsAction } from '../../apiActions/fetchVersionsAction';

import type { AamsData, ICoreTimings, PlayAreaDimensions, VideoConfig } from './types';
import { CoreDefaultTimings } from './types';

export const configSlice = createSlice({
  name: 'config',
  initialState: (): {
    session?: IUserSession | null;
    urlParams: string;
    playAreaSize: number;
    playAreaDimensions: PlayAreaDimensions | null;
    tableConfig: TableConfigType | null;
    tableConfigReady: boolean;
    statsSettings: StatsSettingsType | null | undefined;
    statsConfig: StatsConfigType | null | undefined;
    casinoConfig: CasinoConfigType | null;
    env: EnvConfigType | null;
    playerConfig: PlayerConfigType | null;
    playerConfigReady: boolean;
    compliance: Compliance | null;
    versions: VersionsConfigType | null;
    realityCheck: RealityCheckType | null;
    rtp: RtpType | null;
    timings?: ICoreTimings;
    defaultTimings?: ICoreTimings;
    gameHeaderOverlayHeight?: number;
    isGameVisible: boolean;
    aamsData: AamsData | null;
    videoConfig: VideoConfig | null | undefined;
  } => {
    return {
      session: UserSession.getSession(),
      urlParams: window.location.search,
      playAreaSize: 0, // TODO: deprecate me and use value bellow
      playAreaDimensions: null,
      tableConfig: null,
      tableConfigReady: false,
      statsConfig: undefined,
      statsSettings: undefined,
      casinoConfig: null,
      env: null,
      playerConfig: null,
      playerConfigReady: false,
      compliance: null,
      versions: null,
      realityCheck: null,
      rtp: null,
      timings: undefined,
      defaultTimings: undefined,
      gameHeaderOverlayHeight: 0,
      isGameVisible: true,
      aamsData: null,
      videoConfig: undefined,
    };
  },
  reducers: {
    setPlayAreaSize: (state, action: PayloadAction<number>) => {
      state.playAreaSize = action.payload;
    },
    setPlayAreaDimensions: (state, action: PayloadAction<PlayAreaDimensions>) => {
      state.playAreaDimensions = action.payload;
    },
    setScreenName: (state, action: PayloadAction<string>) => {
      state.playerConfig!.screenName = action.payload;
    },
    setRealityCheck: (state, action: PayloadAction<RealityCheckType | null>) => {
      state.realityCheck = action.payload;
    },
    sessionBalanceWasSet: (state) => {
      if (state.compliance) {
        state.compliance.sessionBalanceWasSet = true;
      }
    },
    setAaamsSessions: (state, action: PayloadAction<SessionBalanceResponse>) => {
      state.aamsData = { aamsTicket: action.payload.aamsTicket, aamsSessionId: action.payload.aamsSessionId };
    },
    setGameServer: (
      state,
      action: PayloadAction<{
        httpAddress: string;
        wsAddress: string;
      }>
    ) => {
      if (state.tableConfig) {
        state.tableConfig.gsAddress = action.payload.httpAddress;
        state.tableConfig.wsAddress = action.payload.wsAddress;
      }
      if (state.session) {
        state.session.actualWebServer = action.payload.httpAddress;
        state.session.socketServer = action.payload.wsAddress;
      }

      const savedSession = SessionStorage.getJson<IUserSession>('PPG');
      if (savedSession) {
        if (savedSession.actual_web_server && savedSession.socket_server) {
          SessionStorage.setJson('PPG', {
            ...savedSession,
            actual_web_server: action.payload.httpAddress,
            socket_server: action.payload.wsAddress,
          });
        }
      }

      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.get('actual_web_server') && searchParams.get('socket_server')) {
        searchParams.set('actual_web_server', action.payload.httpAddress);
        searchParams.set('socket_server', action.payload.wsAddress);
      }
    },
    setDefaultTimings: (state, action: PayloadAction<Partial<ICoreTimings>>) => {
      if (!state.defaultTimings) {
        const combinedTimings = {
          ...CoreDefaultTimings,
          ...action.payload,
        };
        state.defaultTimings = combinedTimings;
        state.timings = combinedTimings;
      }
    },
    setTimings: (state, action: PayloadAction<Partial<ICoreTimings>>) => {
      if (state.timings) {
        state.timings = {
          ...state.timings,
          ...action.payload,
        };
      } else {
        console.error('Setting timings before defining default timings');
      }
    },
    resetTimings: (state, action: PayloadAction<void | (keyof ICoreTimings)[]>) => {
      if (state.timings) {
        if (action.payload) {
          for (const key of action.payload) {
            if (state.defaultTimings) {
              state.timings[key] = state.defaultTimings[key];
            }
          }
        } else {
          state.timings = state.defaultTimings;
        }
      } else {
        console.error('Resetting timings before defining default timings');
      }
    },
    setGameHeaderOverlayHeight: (state, action: PayloadAction<number>) => {
      state.gameHeaderOverlayHeight = action.payload;
    },
    /** Is the game window/tab visible */
    setGameVisible: (state, action: PayloadAction<boolean>) => {
      state.isGameVisible = action.payload;
    },
    setStatsSettings: (state, action: PayloadAction<StatsSettingsType | null>) => {
      state.statsSettings = action.payload;
    },
    setStatsConfig: (state, action: PayloadAction<StatsConfigType | null>) => {
      state.statsConfig = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTableConfigAction.fulfilled, (state, action) => {
      state.tableConfig = action.payload.parsedResponse;

      const response = action.payload.rawResponse;

      state.aamsData = {
        aamsSessionId: response.params.aamsSessionId,
        aamsTicket: response.params.aamsTicket,
      };

      state.videoConfig = {
        enableVideoStats: response.params.enable_video_stats,
        hlsLocation: response.params.hls_location,
        retrieveVideoQuality: response.params.retrive_video_quality ?? true,
        storeVideoQuality: response.params.store_video_quality ?? true,
        videoLocation: response.params.video_location,
        videoPlayerVersion: response.params.video_player_version,
        videoPlayerIOSVersion: response.params.video_player_ios_version,
        iosVideoBandwidthConfig: response.params.ios_video_bandwidth_config ? JSON.parse(response.params.ios_video_bandwidth_config) : [],
        iosAbrConfig: response.params.ios_abr_config ? JSON.parse(response.params.ios_abr_config) : [],
        supportsAV1Codec: response.params.enable_av1_video,
      };

      state.tableConfigReady = true;
    });
    builder.addCase(fetchTableConfigAction.rejected, (state) => {
      state.tableConfig = null;
      state.aamsData = null;
      state.videoConfig = null;
      state.tableConfigReady = true;
    });
    builder.addCase(fetchStatsSettingsAction.fulfilled, (state, action) => {
      state.statsSettings = action.payload;
    });
    builder.addCase(fetchStatsSettingsAction.rejected, (state) => {
      state.statsSettings = null;
    });
    builder.addCase(fetchStatsConfigAction.fulfilled, (state, action) => {
      state.statsConfig = action.payload;
    });
    builder.addCase(fetchStatsConfigAction.rejected, (state) => {
      state.statsConfig = null;
    });
    builder.addCase(fetchCasinoConfigAction.fulfilled, (state, action) => {
      const { parsedResponse } = action.payload;
      state.casinoConfig = parsedResponse;
      if (state.session?.integrationApi) {
        state.casinoConfig.integrationApi = [state.session.integrationApi];
      }
      if (parsedResponse.homeUrl) {
        try {
          new URL(parsedResponse.homeUrl);
        } catch {
          Logger.logFailure('invalidHomeUrl', {
            additional: parsedResponse.homeUrl,
          });
        }
      }
    });
    builder.addCase(fetchEnvAction.fulfilled, (state, action) => {
      state.env = action.payload;
    });
    builder.addCase(fetchPlayerConfigAction.fulfilled, (state, action) => {
      state.playerConfig = action.payload.parsedPlayerConfig;
      state.compliance = action.payload.compliance;

      state.playerConfigReady = true;
    });
    builder.addCase(fetchVersionsAction.fulfilled, (state, action) => {
      state.versions = action.payload;
    });
    builder.addCase(fetchRtpAction.fulfilled, (state, action) => {
      state.rtp = action.payload;
    });
  },
});

export const configActions = configSlice.actions;
